// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Daily Catch  (route /daily-catch)
// The live drop: what staff have landed today and how many packs are left, updating on its own while people watch
// (hooks/useDrop polls /api/public/drop). Staff publish it (backend PUT /api/admin/drop…); a message to customers links
// here, so the page is built for a rush: it loads without sign-in, keeps the last good data through a bad connection,
// and shows scarcity from real stock only. Order on screen: status strip (batch, claimed %, dispatch countdown) →
// category chips (counts of what is live) → landing cards → sticky cart bar.
// Cards price from the menu and open the shared pack-size + cut sheet, capped at packs left (PackSheet `maxTotal`).
// Stock is set by staff; it is NOT yet reduced when an order is placed (orders are still simulated) — see docs/FEATURES.md.
// Deliberately NOT here (nothing supports it): "Certified Cold-Chain Safe / formalin-tested / iced under 60 mins",
// the dock-to-kitchen trail with timestamps, "Express Dispatch"/"free express delivery", per-landing prices that differ
// from the menu, the bottom tab bar.
// ─────────────────────────────────────────────────────────────────────────────
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Fish, LayoutGrid, Shell, Sparkles } from "lucide-react";
import Breadcrumbs from "../components/Breadcrumbs";
import DropStatus from "../components/dailycatch/DropStatus";
import LandingCard from "../components/dailycatch/LandingCard";
import CartBar from "../components/menu/CartBar";
import PackSheet from "../components/menu/PackSheet";
import PageTransition from "../components/PageTransition";
import { seafoodItems, type SeafoodCategory, type SeafoodItem } from "../data/seafood";
import useDocumentTitle from "../hooks/useDocumentTitle";
import useDrop from "../hooks/useDrop";
import usePackSheet from "../hooks/usePackSheet";
import type { Landing } from "../utils/drop";

type Filter = SeafoodCategory | "All";

const CHIPS: { value: Filter; label: string; icon: typeof Fish }[] = [
  { value: "All", label: "All landings", icon: LayoutGrid },
  { value: "Fish", label: "Fish", icon: Fish },
  { value: "Shellfish", label: "Shellfish", icon: Shell },
  { value: "Premium", label: "Premium", icon: Sparkles },
];

export default function DailyCatch() {
  useDocumentTitle("Daily Catch");
  const { data, loading, stale, updatedAt, now, refresh } = useDrop();
  const sheet = usePackSheet();
  const [filter, setFilter] = useState<Filter>("All");

  // A landing whose catch isn't on the menu can't be priced or ordered, so it is skipped rather than shown broken.
  const rows = useMemo(
    () =>
      (data?.landings ?? []).flatMap((landing) => {
        const item = seafoodItems.find((candidate) => candidate.id === landing.itemId);
        return item ? [{ landing, item }] : [];
      }),
    [data],
  );
  const count = (value: Filter) => (value === "All" ? rows.length : rows.filter((row) => row.item.category === value).length);
  const shown = rows.filter((row) => filter === "All" || row.item.category === filter);

  /** Packs left for the catch in the open sheet (a catch has at most one landing; the newest wins). */
  const openId = sheet.sheetProps.item?.id;
  const maxTotal = openId ? rows.find((row) => row.item.id === openId)?.landing.packsLeft : undefined;

  const cardFor = (landing: Landing, item: SeafoodItem) => {
    const props = sheet.cardProps(item);
    return <LandingCard key={landing.id} landing={landing} item={item} inCart={props.inCart} cut={props.cut} onOpen={props.onOpen} />;
  };

  return (
    <PageTransition>
      <div className="bg-foam-50 pb-28 pt-28 dark:bg-ocean-950 sm:pt-32">
        <Breadcrumbs />

        <div className="mx-auto flex max-w-6xl flex-col gap-4 px-4 pt-2">
          <header>
            <h1 className="font-display text-3xl font-bold text-ocean-950 dark:text-foam-50 sm:text-4xl">Daily Catch</h1>
            <p className="mt-1 text-sm text-ocean-800/80 dark:text-foam-100/75">
              What landed at the harbour today, with the packs still available. It updates on its own — no need to refresh.
            </p>
          </header>

          {loading && !data ? (
            <p role="status" className="rounded-2xl bg-white p-6 text-center text-sm text-ocean-800/80 shadow-sm dark:bg-ocean-900 dark:text-foam-100/75">
              Checking the harbour…
            </p>
          ) : !data ? (
            <div role="alert" className="rounded-2xl bg-white p-6 text-center shadow-sm dark:bg-ocean-900">
              <p className="font-display text-lg font-bold">We couldn&apos;t load today&apos;s catch</p>
              <p className="mt-1 text-sm text-ocean-800/80 dark:text-foam-100/75">Check your connection and try again.</p>
              <button type="button" onClick={() => void refresh()} className="mt-4 min-h-11 rounded-full bg-ocean-800 px-6 text-sm font-bold text-white hover:bg-ocean-700">
                Try again
              </button>
            </div>
          ) : !data.drop ? (
            <div className="rounded-2xl bg-white p-8 text-center shadow-sm dark:bg-ocean-900">
              <p className="font-display text-xl font-bold text-ocean-950 dark:text-foam-50">Nothing has landed yet</p>
              <p className="mt-1 text-sm text-ocean-800/80 dark:text-foam-100/75">
                We publish each landing here as the boats come in, and this page fills in by itself. In the meantime the regular menu is always open.
              </p>
              <Link to="/menu" className="mt-4 inline-flex min-h-11 items-center rounded-full bg-ocean-800 px-6 text-sm font-bold text-white hover:bg-ocean-700">
                Browse the menu
              </Link>
            </div>
          ) : (
            <>
              <DropStatus data={data} now={now} updatedAt={updatedAt} stale={stale} />

              <div role="group" aria-label="Filter landings" className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1">
                {CHIPS.map(({ value, label, icon: Icon }) => (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={filter === value}
                    onClick={() => setFilter(value)}
                    className={`inline-flex min-h-11 shrink-0 items-center gap-2 rounded-full px-4 text-sm font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 ${
                      filter === value ? "bg-ocean-800 text-white" : "bg-white text-ocean-900 ring-1 ring-ocean-900/10 hover:bg-ocean-700/5 dark:bg-ocean-900 dark:text-foam-100 dark:ring-white/10"
                    }`}
                  >
                    <Icon className="h-4 w-4" aria-hidden="true" />
                    {label}
                    <span className={`rounded-full px-2 text-xs ${filter === value ? "bg-white/20" : "bg-ocean-700/10 dark:bg-white/10"}`}>{count(value)}</span>
                  </button>
                ))}
              </div>

              {shown.length === 0 ? (
                <div className="rounded-2xl bg-white p-8 text-center shadow-sm dark:bg-ocean-900">
                  <p className="font-display text-lg font-bold">{rows.length === 0 ? "No landings published yet" : "Nothing in this group has landed"}</p>
                  <p className="mt-1 text-sm text-ocean-800/80 dark:text-foam-100/75">This page updates by itself as new landings are added.</p>
                </div>
              ) : (
                <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{shown.map(({ landing, item }) => cardFor(landing, item))}</ul>
              )}
            </>
          )}

          <p className="mt-2 text-center text-xs text-ocean-800/70 dark:text-foam-100/65">
            Prices are the menu&apos;s per-kg prices. Photography courtesy of Wikimedia Commons contributors (CC BY / CC BY-SA). Hover a photo for credit.
          </p>
        </div>

        <PackSheet {...sheet.sheetProps} maxTotal={maxTotal} />
        <CartBar />
      </div>
    </PageTransition>
  );
}
